import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { User, CreateUserRequest, UpdateUserRequest, UserRole } from '../types';
import authService from '../services/authService';
import { Icon, PageHeader, LoadingScreen, EmptyState, AnlegenButton, tableCard, tableScroll, th, td, tableRow } from '../components/ui';
import { markiereNeu, ankunft } from '../utils/ankunft';

// authService wirft Error mit der Backend-Meldung – oder einem englischen Fallback („Failed to …“),
// der dann durch den deutschen Text ersetzt wird.
const meldung = (error: unknown, fallback: string): string => {
  const text = (error as Error)?.message;
  return text && !text.startsWith('Failed to') ? text : fallback;
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    username: '',
    password: '',
    role: UserRole.USER as UserRole,
    active: true
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const userList = await authService.getAllUsers();
      setUsers(userList);
    } catch (error) {
      toast.error(meldung(error, 'Benutzer konnten nicht geladen werden'));
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingUser) {
        const updateData: UpdateUserRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          role: formData.role,
          active: formData.active,
          ...(formData.password && { password: formData.password })
        };

        await authService.updateUser(editingUser.id, updateData);
        markiereNeu(`user-${editingUser.id}`);
        toast.success('Benutzer gespeichert');
        setEditingUser(null);
      } else {
        const createData: CreateUserRequest = {
          firstName: formData.firstName,
          lastName: formData.lastName,
          username: formData.username,
          password: formData.password,
          role: formData.role
        };

        const angelegt = await authService.createUser(createData);
        if (angelegt?.id) markiereNeu(`user-${angelegt.id}`);
        toast.success('Benutzer angelegt');
        setShowCreateForm(false);
      }

      setFormData({
        firstName: '',
        lastName: '',
        username: '',
        password: '',
        role: UserRole.USER,
        active: true
      });

      await loadUsers();
    } catch (error: any) {
      toast.error(meldung(error, 'Benutzer konnte nicht gespeichert werden'));
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      username: user.username,
      password: '',
      role: user.role,
      active: user.active
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (user: User) => {
    if (window.confirm(`Benutzer „${user.firstName} ${user.lastName}“ löschen?`)) {
      try {
        await authService.deleteUser(user.id);
        toast.success('Benutzer gelöscht');
        await loadUsers();
      } catch (error: any) {
        toast.error(meldung(error, 'Benutzer konnte nicht gelöscht werden'));
      }
    }
  };

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      username: '',
      password: '',
      role: UserRole.USER,
      active: true
    });
    setEditingUser(null);
    setShowCreateForm(false);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="p-4 md:p-6">
      <PageHeader
        title="Benutzerverwaltung"
        subtitle={`${users.length} Benutzer`}
        actions={<AnlegenButton onClick={() => setShowCreateForm(true)}>Benutzer anlegen</AnlegenButton>}
      />

      {/* Create/Edit Form Modal */}
      {showCreateForm && (
        <div className="ui-fade fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="ui-pop bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">
              {editingUser ? 'Benutzer bearbeiten' : 'Benutzer anlegen'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Vorname</label>
                  <input
                    type="text"
                    required
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Nachname</label>
                  <input
                    type="text"
                    required
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-lg"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Benutzername</label>
                <input
                  type="text"
                  required
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Passwort {editingUser && '(leer lassen, um es beizubehalten)'}
                </label>
                <input
                  type="password"
                  required={!editingUser}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Rolle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                >
                  <option value={UserRole.USER}>Benutzer</option>
                  <option value={UserRole.ADMIN}>Administrator</option>
                </select>
              </div>

              {editingUser && (
                <div className="mb-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                      className="mr-2"
                    />
                    Aktiv
                  </label>
                </div>
              )}

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-600 text-white rounded-lg hover:bg-brand-700"
                >
                  {editingUser ? 'Speichern' : 'Benutzer anlegen'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Benutzer-Tabelle */}
      <div className={tableCard}>
        {users.length === 0 ? (
          <EmptyState
            icon={<Icon.Schild className="h-6 w-6" />}
            title="Noch keine Benutzer"
            action={<AnlegenButton onClick={() => setShowCreateForm(true)}>Benutzer anlegen</AnlegenButton>}
          />
        ) : (
          <div className={tableScroll}>
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Rolle</th>
                  <th className={th}>Status</th>
                  <th className={th}>Angelegt</th>
                  <th className={`${th} text-right`}><span className="sr-only">Aktionen</span></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((user) => {
                  const a = ankunft(`user-${user.id}`);
                  const markiert = a.className !== '';
                  return (
                    <tr key={user.id} ref={a.ref} className={`${tableRow} ${a.className}`}>
                      <td className={`${td} whitespace-nowrap`}>
                        <div className="flex items-center">
                          <div className="h-8 w-8 rounded-full bg-brand-100 flex items-center justify-center">
                            <span className="text-sm font-medium text-brand-600">
                              {user.firstName[0]}{user.lastName[0]}
                            </span>
                          </div>
                          <div className="ml-3">
                            <div className="font-semibold text-gray-900">{user.firstName} {user.lastName}</div>
                            <div className="text-xs text-gray-500">{user.username}</div>
                          </div>
                        </div>
                      </td>
                      <td className={`${td} whitespace-nowrap`}>
                        <span key={markiert ? user.role : undefined} className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${markiert ? 'ui-wechsel' : ''} ${
                          user.role === UserRole.ADMIN
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                          {user.role === UserRole.ADMIN ? 'Administrator' : 'Benutzer'}
                        </span>
                      </td>
                      <td className={`${td} whitespace-nowrap`}>
                        <span key={markiert ? String(user.active) : undefined} className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${markiert ? 'ui-wechsel' : ''} ${
                          user.active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.active ? 'Aktiv' : 'Inaktiv'}
                        </span>
                      </td>
                      <td className={`${td} whitespace-nowrap text-gray-500`}>
                        {user.createdAt ? new Date(user.createdAt).toLocaleDateString('de-DE') : ''}
                      </td>
                      <td className={`${td} whitespace-nowrap text-right font-medium`}>
                        <button
                          onClick={() => handleEdit(user)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-brand-700 hover:bg-brand-50"
                        >
                          <Icon.Edit className="h-4 w-4" />Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDelete(user)}
                          className="ml-1 inline-flex items-center gap-1 rounded-md px-2 py-1 text-red-600 hover:bg-red-50"
                        >
                          <Icon.Trash className="h-4 w-4" />Löschen
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Users;
